import { state } from '../state';
import type { AssetVehicleRow } from './assets-types';
import { runAssetsAnalysis } from './assets-analysis';
import { renderAssetsResults } from './assets-results';
import { formatHms } from '../utils/time';
import {
  MEASUREMENT_UNIT_EVENT, formatDistance, getMeasurementUnit, setMeasurementUnit,
  type MeasurementUnit,
} from '../utils/units';

// ── Module state ──────────────────────────────────────────────────────────

export let assetsRawRows:  AssetVehicleRow[] = [];
export let assetsSortCol = 'dayUtilPct';
export let assetsSortAsc = false;
let assetsInitialized = false;

export function setAssetsSortCol(col: string): void  { assetsSortCol = col; }
export function setAssetsSortAsc(asc: boolean): void { assetsSortAsc = asc; }
export function setAssetsRawRows(rows: AssetVehicleRow[]): void { assetsRawRows = rows; }

// ── Formatters ────────────────────────────────────────────────────────────

export function fmtPct(n: number): string {
  return n.toFixed(1) + '%';
}

export function fmtHrs(h: number): string {
  return formatHms(h);
}

export function fmtKm(km: number): string {
  return formatDistance(km, { zeroAsDash: true });
}

export function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ── Page init ─────────────────────────────────────────────────────────────

export function initAssetsPage(): void {
  if (!assetsInitialized) {
    auSetRange('30d');
    assetsInitialized = true;
  }

  if (state.allVehicles.length) buildGroupSelector();

  const runBtn = document.getElementById('auRunBtn') as HTMLButtonElement & { _wired?: boolean };
  if (runBtn._wired) return;
  runBtn._wired = true;

  runBtn.addEventListener('click', runAssetsAnalysis);

  const applyUnit = (unit: MeasurementUnit): void => {
    document.getElementById('auUnitKm')?.classList.toggle('active', unit === 'km');
    document.getElementById('auUnitMi')?.classList.toggle('active', unit === 'mi');
    auRecalculate();
  };
  applyUnit(getMeasurementUnit());
  document.getElementById('auUnitKm')!.addEventListener('click', () => setMeasurementUnit('km'));
  document.getElementById('auUnitMi')!.addEventListener('click', () => setMeasurementUnit('mi'));
  window.addEventListener(MEASUREMENT_UNIT_EVENT, (e) =>
    applyUnit((e as CustomEvent<MeasurementUnit>).detail));

  document.getElementById('auExportBtn')!.addEventListener('click', async () => {
    const { exportAssetsXlsx } = await import('./assets-export');
    exportAssetsXlsx();
  });

  document.querySelectorAll<HTMLButtonElement>('.au-qr-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.querySelectorAll<HTMLButtonElement>('.au-qr-btn').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      auSetRange(btn.dataset.range!);
    });
  });

  // Group selector dropdown
  const gsToggle = document.getElementById('auGsToggle')!;
  const gsPanel  = document.getElementById('auGsPanel')!;
  gsToggle.addEventListener('click', () => gsPanel.classList.toggle('hidden'));
  document.addEventListener('click', (e) => {
    const gs = document.getElementById('auGroupSelector');
    if (gs && !gs.contains(e.target as Node)) gsPanel.classList.add('hidden');
  });

  document.getElementById('auGsAll')!.addEventListener('click',  auSelectAllGroups);
  document.getElementById('auGsNone')!.addEventListener('click', auSelectNoneGroups);
}

export function resetAssetsPage(): void {
  assetsRawRows    = [];
  assetsInitialized = false;

  document.getElementById('auEmpty')?.classList.remove('hidden');
  document.getElementById('auResults')?.classList.add('hidden');
  document.getElementById('auErrBox')?.classList.add('hidden');
  document.getElementById('auWarnBanner')?.classList.add('hidden');

  const exportBtn = document.getElementById('auExportBtn') as HTMLElement | null;
  if (exportBtn) exportBtn.style.display = 'none';

  const runBtn = document.getElementById('auRunBtn') as HTMLButtonElement & { _wired?: boolean } | null;
  if (runBtn) runBtn._wired = false;

  // Otherwise the previous database's group checkboxes stay in the DOM: initAssetsPage() only
  // rebuilds this list when state.allVehicles is already populated, which isn't true right after
  // a fresh login, and nothing else re-triggers a rebuild if the user is already on this page.
  const gsList = document.getElementById('auGsList');
  if (gsList) gsList.innerHTML = '';
  const gsLabel = document.getElementById('auGsLabel');
  if (gsLabel) gsLabel.textContent = 'Loading…';
}

// ── Date range ────────────────────────────────────────────────────────────

function auSetRange(range: string): void {
  const now = new Date();
  // Use local-date constructor so date arithmetic stays in local timezone,
  // avoiding UTC-offset shifting that breaks toISOString().slice(0,10).
  const toLocal   = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const fromLocal = new Date(toLocal);

  // setDate() handles month/year rollovers and DST automatically.
  // N-1 days back from today gives exactly N inclusive days.
  if (range === '7d')  fromLocal.setDate(fromLocal.getDate() - 6);
  if (range === '14d') fromLocal.setDate(fromLocal.getDate() - 13);
  if (range === '30d') fromLocal.setDate(fromLocal.getDate() - 29);
  if (range === '90d') fromLocal.setDate(fromLocal.getDate() - 89);
  if (range === 'mtd') fromLocal.setDate(1);

  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  (document.getElementById('auFrom') as HTMLInputElement).value = fmt(fromLocal);
  (document.getElementById('auTo')   as HTMLInputElement).value = fmt(toLocal);
}

// ── Group selector ────────────────────────────────────────────────────────

export function buildGroupSelector(): void {
  const list = document.getElementById('auGsList')!;
  list.innerHTML = '';

  const groups = [...new Set(state.allVehicles.map((v) => v.group || 'No Group'))].sort((a, b) => {
    if (a === 'No Group') return 1;
    if (b === 'No Group') return -1;
    return a.localeCompare(b);
  });

  groups.forEach((g) => {
    const count = state.allVehicles.filter((v) => (v.group || 'No Group') === g).length;
    const lbl   = document.createElement('label');
    lbl.className = 'vselector-item';
    const cb  = document.createElement('input');
    cb.type    = 'checkbox';
    cb.checked = true;
    (cb as HTMLInputElement & { dataset: DOMStringMap }).dataset.group = g;
    cb.addEventListener('change', updateGroupSelectorLabel);
    const nm = document.createElement('span');
    nm.className   = 'vselector-item-name';
    nm.textContent = `${g} (${count})`;
    lbl.appendChild(cb);
    lbl.appendChild(nm);
    list.appendChild(lbl);
  });

  updateGroupSelectorLabel();
}

function updateGroupSelectorLabel(): void {
  const all     = document.querySelectorAll<HTMLInputElement>('#auGsList input[type="checkbox"]');
  const checked = Array.from(all).filter((c) => c.checked).length;
  const lbl     = document.getElementById('auGsLabel');
  if (lbl) {
    lbl.textContent = checked === all.length
      ? `All ${all.length} groups`
      : `${checked} of ${all.length} groups`;
  }
}

function auSelectAllGroups(): void {
  document.querySelectorAll<HTMLInputElement>('#auGsList input').forEach((c) => { c.checked = true; });
  updateGroupSelectorLabel();
}

function auSelectNoneGroups(): void {
  document.querySelectorAll<HTMLInputElement>('#auGsList input').forEach((c) => { c.checked = false; });
  updateGroupSelectorLabel();
}

export function getSelectedGroups(): Set<string> {
  return new Set(
    Array.from(document.querySelectorAll<HTMLInputElement>('#auGsList input:checked')).map(
      (c) => (c as HTMLInputElement & { dataset: DOMStringMap }).dataset.group!
    )
  );
}

// ── Recalculate (re-render on sort change) ────────────────────────────────

export function auRecalculate(): void {
  if (!assetsRawRows.length) return;
  renderAssetsResults(assetsRawRows, assetsSortCol, assetsSortAsc);
}
